import { useCallback, useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';
import { imageService } from '../api/images';
import { albumService } from '../api/albums';
import { GallerySort, GalleryTypeFilter, Image } from '../types/entities/image.entity';

interface UseGalleryImagesOptions {
    typeFilter: GalleryTypeFilter;
    sort: GallerySort;
    albumId?: string;
}

export function useGalleryImages({ typeFilter, sort, albumId }: UseGalleryImagesOptions) {
    const { t } = useTranslation();
    const { enqueueSnackbar } = useSnackbar();
    const queryClient = useQueryClient();
    const [page, setPage] = useState(1);
    const [allImages, setAllImages] = useState<Image[]>([]);
    const [hasMore, setHasMore] = useState(true);

    useEffect(() => {
        setPage(1);
        setAllImages([]);
        setHasMore(true);
    }, [typeFilter, sort, albumId]);

    const { data, isLoading, error, isFetching } = useQuery({
        queryKey: ['images', page, typeFilter, sort, albumId],
        queryFn: () => imageService.getAll({
            page,
            type: typeFilter,
            sort,
            albumId,
            perPage: 24,
        }),
    });

    useEffect(() => {
        if (!data?.data) {
            return;
        }

        const incoming = data.data.images;
        if (page === 1) {
            setAllImages(incoming);
        } else {
            setAllImages((prev) => {
                const existingIds = new Set(prev.map((img) => img.id));
                const newImages = incoming.filter((img) => !existingIds.has(img.id));
                return [...prev, ...newImages];
            });
        }
        setHasMore(data.data.meta.currentPage < data.data.meta.lastPage);
    }, [data, page]);

    const loadMore = useCallback(() => {
        if (hasMore && !isFetching) {
            setPage((prev) => prev + 1);
        }
    }, [hasMore, isFetching]);

    const refreshList = useCallback(() => {
        setPage(1);
        setAllImages([]);
        setHasMore(true);
        queryClient.invalidateQueries({ queryKey: ['images'] });
    }, [queryClient]);

    const removeImage = useCallback((id: string) => {
        setAllImages((prev) => prev.filter((img) => img.id !== id));
    }, []);

    const uploadMutation = useMutation({
        mutationFn: ({ files, albumId: uploadAlbumId }: { files: File[]; albumId?: string | null }) =>
            imageService.upload(files, uploadAlbumId),
        onSuccess: () => {
            setPage(1);
            setAllImages([]);
            queryClient.invalidateQueries({ queryKey: ['images'] });
            queryClient.invalidateQueries({ queryKey: ['albums'] });
            enqueueSnackbar(t('gallery.uploadSuccess'), { variant: 'success' });
        },
        onError: (uploadError: any) => {
            const errorMessage =
                uploadError.response?.data?.errors?.images?.[0]
                || uploadError.response?.data?.message
                || t('gallery.uploadError');
            enqueueSnackbar(errorMessage, { variant: 'error' });
        },
    });

    const deleteMutation = useMutation({
        mutationFn: imageService.delete,
        onSuccess: (_result, deletedId) => {
            removeImage(deletedId);
            queryClient.invalidateQueries({ queryKey: ['images'] });
            queryClient.invalidateQueries({ queryKey: ['albums'] });
            enqueueSnackbar(t('gallery.deleteSuccess'), { variant: 'success' });
        },
        onError: () => {
            enqueueSnackbar(t('gallery.deleteError'), { variant: 'error' });
        },
    });

    const detachFromAlbumMutation = useMutation({
        mutationFn: (imageId: string) => albumService.detachImage(albumId!, imageId),
        onSuccess: (_result, imageId) => {
            removeImage(imageId);
            queryClient.invalidateQueries({ queryKey: ['images'] });
            queryClient.invalidateQueries({ queryKey: ['albums'] });
            if (albumId) {
                queryClient.invalidateQueries({ queryKey: ['album', albumId] });
            }
            enqueueSnackbar(t('gallery.removeFromAlbumSuccess'), { variant: 'success' });
        },
        onError: () => {
            enqueueSnackbar(t('gallery.removeFromAlbumError'), { variant: 'error' });
        },
    });

    const saveDisplayName = useCallback(async (id: string, displayName: string) => {
        await imageService.updateDisplayName(id, displayName);
        setAllImages((prev) =>
            prev.map((img) => (img.id === id ? { ...img, display_name: displayName } : img)),
        );
        enqueueSnackbar(t('gallery.displayNameSaved'), { variant: 'success' });
    }, [enqueueSnackbar, t]);

    return {
        allImages,
        hasMore,
        isLoading: isLoading && page === 1 && allImages.length === 0,
        isFetching,
        error,
        loadMore,
        refreshList,
        uploadMutation,
        deleteMutation,
        detachFromAlbumMutation,
        saveDisplayName,
    };
}
